import classNames from 'classnames'
import { TextField } from 'formik-mui'
import { get, isEmpty, isNil } from 'lodash'
import DeleteIcon from 'mdi-react/DeleteIcon'
import React from 'react'
import { DigitalPresence } from 'scenes/Dashboard/data/digitalPresences/types'
import { removeItemByIndex, replaceObjectInArray } from 'utils-next/arrays'
import { connect, FastField, Field, FieldProps, FormProps } from 'utils-next/forms'

import { IconButton, Paper, Theme, Typography } from '@mui/material'
import { createStyles, WithStyles, withStyles } from '@mui/styles'

import { OrganizationFormValues } from '../../OrganizationDetailsEdit.models'
import { FileInput } from './components/FileInput.component'

type AttachmentOuterProps = {
  attachment: DigitalPresence
  className?: string
  index: number
}

type AttachmentProps = WithStyles<typeof styles> & {
  formik: FormProps<OrganizationFormValues>
} & AttachmentOuterProps

class Attachment extends React.Component<AttachmentProps> {
  private name = `attachmentsValues[${this.props.index}]`

  constructor(props: AttachmentProps) {
    super(props)

    this.onDeleteClick = this.onDeleteClick.bind(this)
    this.onUploadButtonChange = this.onUploadButtonChange.bind(this)
    this.onValidateLabelInput = this.onValidateLabelInput.bind(this)
    this.onValidateUploadButton = this.onValidateUploadButton.bind(this)
  }

  public render() {
    const {
      attachment,
      className,
      classes,
      formik: { errors, isSubmitting },
    } = this.props

    const isReadOnly = !!attachment.id

    return (
      <Paper elevation={2} className={classNames(className, classes.container)}>
        <FastField
          autoFocus
          className={classes.textField}
          component={TextField}
          disabled={isReadOnly}
          fullWidth
          label="Name"
          name={`${this.name}.label`}
          placeholder="Pitch Deck"
          validate={this.onValidateLabelInput}
          InputLabelProps={{ shrink: true }}
        />

        {isReadOnly && typeof attachment.value === 'string' ? (
          <Typography className={classes.textField} variant="body1">
            <a href={attachment.value} target="blank">
              See attachment
            </a>
          </Typography>
        ) : null}

        {!isReadOnly && (
          <Field
            name={`${this.name}.value`}
            render={({ field: { name, value } }: FieldProps) => (
              <>
                <FileInput
                  disabled={isReadOnly || isSubmitting}
                  error={get(errors, name)}
                  label="Choose a file to upload or a url"
                  onChange={this.onUploadButtonChange}
                  value={value}
                />
              </>
            )}
            validate={this.onValidateUploadButton}
          />
        )}

        <footer className={classes.footer}>
          <IconButton aria-label="Delete funding round" size="small" onClick={this.onDeleteClick}>
            <DeleteIcon />
          </IconButton>
        </footer>
      </Paper>
    )
  }

  private onDeleteClick() {
    const {
      attachment,
      formik: {
        setFieldValue,
        values: { attachmentsValues },
      },
      index,
    } = this.props

    if (isNil(attachment.id)) {
      setFieldValue('attachmentsValues', removeItemByIndex(attachmentsValues, index))

      return
    }

    const removableAttachment: DigitalPresence = { ...attachment, toDelete: true }

    setFieldValue('attachmentsValues', replaceObjectInArray(attachmentsValues, index, removableAttachment))
  }

  private onUploadButtonChange(file: File | string) {
    this.props.formik.setFieldValue(`${this.name}.value`, file)
  }

  private onValidateLabelInput(value: string) {
    if (isEmpty(value)) {
      return 'Attachments should have a name'
    }

    const { attachmentsValues: attachments } = this.props.formik.values

    const { length: numberOfAttachmentsWithSameLabel } = attachments.filter(({ label }) => {
      if (!label) {
        return false
      }

      return label.trim() === value.trim()
    })

    if (numberOfAttachmentsWithSameLabel > 1) {
      return 'There is another attachment with the same name'
    }

    return undefined
  }

  private onValidateUploadButton(file: File | null) {
    if (isNil(file)) {
      return `Upload a file for "${this.props.formik.values.attachmentsValues[this.props.index].label}"`
    }

    return undefined
  }
}

const styles = ({ spacing }: Theme) =>
  createStyles({
    container: {
      padding: `${spacing(2)}px ${spacing(3)}px`,
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: spacing(2),
    },
    textField: {
      marginBottom: spacing(2),
    },
  })

export default connect<AttachmentOuterProps, OrganizationFormValues>(withStyles(styles)(Attachment))
